import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { PiWarningCircleLight, PiChartBarLight, PiBriefcaseLight, PiCheckCircleLight, PiDatabaseLight, PiDownloadSimpleLight, PiFileArrowDownLight, PiSignatureLight, PiFunnelLight, PiCircleNotchLight, PiPrinterLight, PiMagnifyingGlassLight, PiShieldCheckLight, PiTableLight, PiTrendUpLight, PiUsersLight } from 'react-icons/pi';
import {
  assessmentAPI,
  candidateAPI,
  dashboardAPI,
  interviewAPI,
  jobAPI,
  offerAPI
} from '../../services/api';

const timeframeOptions = [
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: '90d', label: '90D' },
  { value: '1y', label: '1Y' }
];

const reportTabs = [
  { id: 'jobs', label: 'Jobs' },
  { id: 'candidates', label: 'Candidates' },
  { id: 'interviews', label: 'Interviews' },
  { id: 'assessments', label: 'Assessments' },
  { id: 'offers', label: 'Offers' },
  { id: 'activity', label: 'Activity' }
];

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatPercent = (value) => `${Math.round(Number(value) || 0)}%`;

const formatMoney = (amount, currency = 'USD') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'Not set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(numeric);
};

const formatDate = (value, fallback = '-') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

const formatDateTime = (value, fallback = '-') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

const labelize = (value) => (
  String(value || 'Not set')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const extractError = (error, fallback) => (
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

const candidateName = (match) => {
  const candidate = match?.candidate || {};
  return candidate.fullName || [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'Candidate';
};

const offerCandidateName = (offer) => {
  const candidate = offer?.candidate || {};
  return [candidate.firstName, candidate.lastName].filter(Boolean).join(' ') || 'Candidate';
};

const getSkillName = (skill) => (
  typeof skill === 'string'
    ? skill
    : skill?.name || skill?.skill_name || skill?.skill || skill?.label || 'Skill'
);

const csvEscape = (value) => {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const toCsv = (rows, columns) => {
  const header = columns.map((column) => csvEscape(column.header)).join(',');
  const body = rows.map((row) => columns.map((column) => {
    const value = typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor];
    return csvEscape(value);
  }).join(','));
  return [header, ...body].join('\n');
};

const downloadText = (filename, content, type = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const getPayload = (result, fallback = {}) => (result.status === 'fulfilled' ? result.value : fallback);

const fetchPaginated = async ({ request, key, limit = 100, maxRows = 500 }) => {
  let offset = 0;
  let hasMore = false;
  const rows = [];

  do {
    const response = await request({ limit, offset });
    rows.push(...(Array.isArray(response?.[key]) ? response[key] : []));
    hasMore = Boolean(response?.pagination?.hasMore) && rows.length < maxRows;
    offset += limit;
  } while (hasMore);

  return rows;
};

const buildSummaryRows = (dashboard, jobAnalytics) => {
  const summary = dashboard?.summary || {};
  const analytics = jobAnalytics?.analytics || {};
  return [
    { section: 'Jobs', metric: 'Total jobs', value: summary.totalJobs ?? analytics.jobs?.total ?? 0 },
    { section: 'Jobs', metric: 'Active jobs', value: summary.activeJobs ?? analytics.jobs?.active ?? 0 },
    { section: 'Jobs', metric: 'Open positions', value: summary.openPositions ?? 0 },
    { section: 'Jobs', metric: 'Fill rate', value: `${summary.fillRate ?? analytics.jobs?.fillRate ?? 0}%` },
    { section: 'Candidates', metric: 'Total matches', value: summary.totalMatches ?? analytics.matches?.total ?? 0 },
    { section: 'Candidates', metric: 'Average match score', value: `${summary.averageMatchScore ?? analytics.matches?.averageScore ?? 0}%` },
    { section: 'Candidates', metric: 'Mutual rate', value: `${summary.mutualRate ?? analytics.matches?.mutualRate ?? 0}%` },
    { section: 'Interviews', metric: 'Total interviews', value: summary.totalInterviews ?? 0 },
    { section: 'Interviews', metric: 'Upcoming interviews', value: summary.upcomingInterviews ?? 0 },
    { section: 'Assessments', metric: 'Active assessments', value: summary.activeAssessments ?? 0 },
    { section: 'Assessments', metric: 'Passed assessments', value: summary.assessmentsPassed ?? 0 },
    { section: 'Offers', metric: 'Offers sent', value: summary.offersSent ?? analytics.offers?.sent ?? 0 },
    { section: 'Offers', metric: 'Offers accepted', value: summary.offersAccepted ?? analytics.offers?.accepted ?? 0 },
    { section: 'Offers', metric: 'Offer acceptance rate', value: `${summary.offerAcceptanceRate ?? analytics.offers?.acceptanceRate ?? 0}%` },
    { section: 'Offers', metric: 'Average offer amount', value: summary.averageOfferAmount ?? 0 }
  ];
};

const makeReportFilename = (prefix, timeframe, ext) => {
  const stamp = new Date().toISOString().slice(0, 10);
  return `sanspath-${prefix}-${timeframe}-${stamp}.${ext}`;
};

const Metric = ({ icon: Icon, label, value, detail, tone = 'sky' }) => {
  const tones = {
    sky: 'text-sky-600',
    slate: 'text-slate-800',
    green: 'text-emerald-600',
    amber: 'text-amber-600',
    rose: 'text-rose-600'
  };

  return (
    <div className="min-w-0 rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className={`flex shrink-0 items-center justify-center ${tones[tone] || tones.sky}`}>
          <Icon size={30} />
        </span>
        <span className="truncate text-xs font-medium text-slate-400">{detail}</span>
      </div>
      <div className="truncate font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
      <div className="mt-2 truncate text-sm font-medium text-slate-700">{label}</div>
    </div>
  );
};

const Panel = ({ icon: Icon, title, subtitle, children, action, className = '' }) => (
  <section className={`min-w-0 rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)] ${className}`}>
    <div className="border-b border-slate-100 px-4 py-3 sm:px-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {Icon && (
            <span className="flex shrink-0 items-center justify-center text-sky-600">
              <Icon size={30} />
            </span>
          )}
          <div className="min-w-0">
            <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
            {subtitle && <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
    </div>
    <div className="p-4 sm:p-5">{children}</div>
  </section>
);

const EmptyState = ({ label }) => (
  <div className="flex min-h-[180px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-sm text-slate-500">
    {label}
  </div>
);

const SkeletonBlock = ({ className = '', style }) => (
  <div 
    className={`relative overflow-hidden bg-slate-200/60 ${className}`} 
    style={style}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

const ReportsSkeleton = () => (
  <div className="min-h-screen bg-white px-4 py-6 font-sans sm:px-6 lg:px-8" aria-label="Loading reports">
    <style>{`
      @keyframes shimmer {
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 1.5s infinite;
      }
    `}</style>
    <div className="mx-auto max-w-[1600px] space-y-6">
      {/* Header Skeleton */}
      <div className="rounded-lg border border-slate-200/70 bg-white/85 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.035)]">
        <SkeletonBlock className="h-6 w-36 rounded-md" />
        <SkeletonBlock className="mt-3 h-8 w-48 max-w-full rounded-md" />
        <SkeletonBlock className="mt-3 h-4 w-[520px] max-w-full rounded-md" />
        <div className="mt-5 flex flex-wrap gap-2">
          {[...Array(4)].map((_, item) => <SkeletonBlock key={item} className="h-9 w-14 rounded-md" />)}
        </div>
      </div>

      {/* Toolbar Skeleton */}
      <div className="rounded-lg border border-slate-200/70 bg-white/85 p-3 shadow-[0_14px_36px_rgba(15,23,42,0.035)]">
        <div className="flex flex-col gap-3 lg:flex-row">
          <SkeletonBlock className="h-10 min-w-0 flex-1 rounded-lg" />
          <div className="grid grid-cols-2 gap-2 sm:flex">
            {[...Array(4)].map((_, item) => <SkeletonBlock key={item} className="h-10 w-full rounded-lg sm:w-32" />)}
          </div>
        </div>
      </div>

      {/* Metrics Grid Skeleton */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, item) => (
          <div key={item} className="rounded-lg border border-slate-200/70 bg-white/85 p-4 shadow-[0_12px_30px_rgba(15,23,42,0.035)]">
            <div className="mb-4 flex items-center justify-between">
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-4 w-16 rounded-md" />
            </div>
            <SkeletonBlock className="mb-2 h-7 w-24 rounded-md" />
            <SkeletonBlock className="h-3 w-32 rounded-md" />
          </div>
        ))}
      </div>

      {/* Main Analysis Section Skeleton */}
      <div className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-slate-200/70 bg-white/85 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.035)]">
          <SkeletonBlock className="h-5 w-40 max-w-full rounded-md" />
          <SkeletonBlock className="mt-3 h-3 w-64 max-w-full rounded-md" />
          <div className="mt-8 space-y-4">
            {[74, 58, 44, 30].map((width, index) => (
              <div key={index} className="grid grid-cols-[90px_minmax(0,1fr)_48px] items-center gap-3">
                <SkeletonBlock className="h-4 rounded-md" />
                <SkeletonBlock className="h-8 rounded-lg" style={{ width: `${width}%` }} />
                <SkeletonBlock className="h-4 rounded-md" />
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-slate-200/70 bg-white/85 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.035)]">
          <SkeletonBlock className="h-5 w-36 max-w-full rounded-md" />
          <SkeletonBlock className="mt-3 h-3 w-56 max-w-full rounded-md" />
          <div className="mt-6 space-y-3">
            {[...Array(5)].map((_, item) => <SkeletonBlock key={item} className="h-10 w-full rounded-md" />)}
          </div>
        </div>
      </div>

      {/* 4-Panel Grid Skeleton */}
      <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, item) => (
          <div key={item} className="rounded-lg border border-slate-200/70 bg-white/85 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.035)]">
            <SkeletonBlock className="h-5 w-32 max-w-full rounded-md" />
            <div className="mt-6 space-y-4">
              {[84, 64, 48, 34].map((width, index) => (
                 <div key={index}>
                    <div className="mb-2 flex justify-between">
                        <SkeletonBlock className="h-3 w-1/3 rounded-md" />
                        <SkeletonBlock className="h-3 w-1/5 rounded-md" />
                    </div>
                    <SkeletonBlock className="h-2 rounded-full" style={{ width: `${width}%` }} />
                 </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Large Table Panel Skeleton */}
      <div className="rounded-lg border border-slate-200/70 bg-white/85 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.035)]">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-3">
            <SkeletonBlock className="h-5 w-44 rounded-md" />
            <SkeletonBlock className="h-3 w-72 max-w-full rounded-md" />
          </div>
          <div className="flex flex-wrap gap-2">
            {[...Array(6)].map((_, item) => <SkeletonBlock key={item} className="h-8 w-20 rounded-md" />)}
          </div>
        </div>
        <div className="mt-6 space-y-3">
           <SkeletonBlock className="h-12 w-full rounded-md" />
          {[...Array(5)].map((_, item) => <SkeletonBlock key={item} className="h-12 w-full rounded-md bg-slate-100/50" />)}
        </div>
      </div>
    </div>
  </div>
);

const DistributionBars = ({ rows, valueKey = 'value', labelKey = 'label', maxValue }) => {
  const items = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const max = maxValue || Math.max(...items.map((item) => Number(item[valueKey]) || 0), 1);

  if (!items.length) return <EmptyState label="No distribution data available" />;

  return (
    <div className="space-y-3">
      {items.map((item, index) => {
        const value = Number(item[valueKey]) || 0;
        const width = Math.max(4, Math.round((value / max) * 100));
        return (
          <div key={`${item[labelKey]}-${index}`}>
            <div className="mb-1 flex items-center justify-between gap-3 text-xs">
              <span className="truncate font-medium text-slate-600">{labelize(item[labelKey])}</span>
              <span className="shrink-0 text-slate-400">{formatNumber(value)}{item.percentage ? ` · ${formatPercent(item.percentage)}` : ''}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-sky-500" style={{ width: `${width}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Funnel = ({ rows }) => {
  const items = Array.isArray(rows) ? rows : [];
  const max = Math.max(...items.map((item) => Number(item.count) || 0), 1);

  if (!items.length) return <EmptyState label="No funnel data available" />;

  return (
    <div className="space-y-2">
      {items.map((item) => (
        <div key={item.label} className="grid grid-cols-[130px_minmax(0,1fr)_72px] items-center gap-3 text-sm">
          <div className="truncate text-slate-600">{item.label}</div>
          <div className="h-8 overflow-hidden rounded-lg bg-slate-100">
            <div
              className="flex h-full items-center justify-end rounded-lg bg-[#0b1f3a] px-2 text-xs font-medium text-white"
              style={{ width: `${Math.max(6, Math.round(((Number(item.count) || 0) / max) * 100))}%` }}
            >
              {formatPercent(item.percentage)}
            </div>
          </div>
          <div className="text-right font-medium text-[#0b1f3a]">{formatNumber(item.count)}</div>
        </div>
      ))}
    </div>
  );
};

const DataTable = ({ rows, columns, emptyLabel }) => {
  const source = Array.isArray(rows) ? rows : [];
  if (!source.length) return <EmptyState label={emptyLabel || 'No rows available'} />;

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200">
      <table className="w-full min-w-[980px] text-left">
        <thead className="border-b border-slate-100 bg-slate-50">
          <tr>
            {columns.map((column) => (
              <th key={column.header} className="px-3 py-3 text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {source.map((row, index) => (
            <tr key={`${row.id || row.matchId || row.attemptId || row.title || row.metric || 'row'}-${index}`} className="border-b border-slate-100 last:border-0">
              {columns.map((column) => {
                const value = typeof column.accessor === 'function' ? column.accessor(row) : row[column.accessor];
                return (
                  <td key={column.header} className="px-3 py-3 text-sm text-slate-600">
                    {column.render ? column.render(value, row) : value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReportsPage = () => {
  const [timeframe, setTimeframe] = useState('90d');
  const [activeTab, setActiveTab] = useState('jobs');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [sourceErrors, setSourceErrors] = useState([]);
  const loadedRef = useRef(false);
  const [report, setReport] = useState({
    dashboard: null,
    jobAnalytics: null,
    jobs: [],
    candidates: [],
    interviews: [],
    assessments: [],
    offers: []
  });

  const loadReport = useCallback(async () => {
    const isInitialLoad = !loadedRef.current;
    if (isInitialLoad) setLoading(true);
    else setUpdating(true);
    setError('');
    setSourceErrors([]);

    try {
      const results = await Promise.allSettled([
        dashboardAPI.getOverview(timeframe),
        jobAPI.getAnalytics({ timeframe }),
        fetchPaginated({
          key: 'jobs',
          request: (params) => jobAPI.getEmployerJobs({
            ...params,
            sortBy: 'updated_at',
            sortOrder: 'DESC'
          })
        }),
        fetchPaginated({
          key: 'candidates',
          request: (params) => candidateAPI.getEmployerCandidates({
            ...params,
            minScore: 0,
            sortBy: 'updated_at',
            sortOrder: 'DESC'
          })
        }),
        fetchPaginated({
          key: 'interviews',
          request: (params) => interviewAPI.getAll(params)
        }),
        assessmentAPI.getEmployerAssessments({}),
        fetchPaginated({
          key: 'offers',
          request: (params) => offerAPI.getEmployerOffers(params)
        })
      ]);

      const names = ['dashboard', 'job analytics', 'jobs', 'candidates', 'interviews', 'assessments', 'offers'];
      const failures = results
        .map((result, index) => result.status === 'rejected' ? `${labelize(names[index])}: ${extractError(result.reason, 'Failed to load source')}` : null)
        .filter(Boolean);

      setSourceErrors(failures);
      setReport({
        dashboard: getPayload(results[0], null),
        jobAnalytics: getPayload(results[1], null),
        jobs: getPayload(results[2], []),
        candidates: getPayload(results[3], []),
        interviews: getPayload(results[4], []),
        assessments: getPayload(results[5], { assessments: [] }).assessments || [],
        offers: getPayload(results[6], [])
      });
    } catch (loadError) {
      setError(extractError(loadError, 'Failed to build reports'));
    } finally {
      loadedRef.current = true;
      setLoading(false);
      setUpdating(false);
    }
  }, [timeframe]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const dashboard = report.dashboard || EMPTY_OBJECT;
  const summary = dashboard.summary || EMPTY_OBJECT;
  const charts = dashboard.charts || EMPTY_OBJECT;
  const lists = dashboard.lists || EMPTY_OBJECT;
  const insights = Array.isArray(dashboard.insights) ? dashboard.insights : EMPTY_ARRAY;
  const jobAnalytics = report.jobAnalytics || EMPTY_OBJECT;

  const tableRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = {
      jobs: report.jobs,
      candidates: report.candidates,
      interviews: report.interviews,
      assessments: report.assessments,
      offers: report.offers,
      activity: lists.recentActivity || []
    };

    if (!query) return rows;

    const matches = (row) => JSON.stringify(row || {}).toLowerCase().includes(query);
    return Object.fromEntries(Object.entries(rows).map(([key, value]) => [key, value.filter(matches)]));
  }, [report, lists.recentActivity, search]);

  const columns = useMemo(() => ({
    jobs: [
      { header: 'Job', accessor: (row) => row.title },
      { header: 'Status', accessor: (row) => labelize(row.status) },
      { header: 'Type', accessor: (row) => labelize(row.jobType) },
      { header: 'Location', accessor: (row) => row.location || (row.isRemoteAllowed ? 'Remote' : '-') },
      { header: 'Positions', accessor: (row) => `${formatNumber(row.positionsFilled)} / ${formatNumber(row.positionsAvailable)}` },
      { header: 'Matches', accessor: (row) => row.stats?.totalMatches || 0 },
      { header: 'Mutual', accessor: (row) => row.stats?.mutualMatches || 0 },
      { header: 'Offers', accessor: (row) => `${row.stats?.offersSent || 0} sent · ${row.stats?.offersAccepted || 0} accepted` },
      { header: 'Avg Score', accessor: (row) => row.stats?.averageMatchScore === null ? '-' : `${row.stats?.averageMatchScore || 0}%` },
      { header: 'Updated', accessor: (row) => formatDate(row.updatedAt) }
    ],
    candidates: [
      { header: 'Candidate', accessor: (row) => candidateName(row) },
      { header: 'Role', accessor: (row) => row.job?.title || '-' },
      { header: 'Status', accessor: (row) => labelize(row.matchStatus) },
      { header: 'Band', accessor: (row) => row.matchBand || '-' },
      { header: 'Score', accessor: (row) => `${Math.round(Number(row.overallMatchScore) || 0)}%` },
      { header: 'Experience', accessor: (row) => `${labelize(row.candidate?.experienceLevel)} · ${row.candidate?.experienceYears ?? 0} yrs` },
      { header: 'Location', accessor: (row) => row.candidate?.location || '-' },
      { header: 'Workflow', accessor: (row) => `${row.workflow?.interviews?.total || 0} interviews · ${row.workflow?.assessments?.total || 0} assessments · ${row.workflow?.offers?.total || 0} offers` },
      { header: 'Skills', accessor: (row) => (row.candidate?.skills || []).slice(0, 5).map(getSkillName).join(', ') || '-' }
    ],
    interviews: [
      { header: 'Candidate', accessor: (row) => row.candidate?.name || '-' },
      { header: 'Role', accessor: (row) => row.job?.title || '-' },
      { header: 'Title', accessor: (row) => row.title || '-' },
      { header: 'Type', accessor: (row) => labelize(row.type) },
      { header: 'Status', accessor: (row) => labelize(row.status) },
      { header: 'Result', accessor: (row) => labelize(row.resultStatus || row.employerResult?.recommendation || '-') },
      { header: 'Scheduled', accessor: (row) => formatDateTime(row.scheduledDate) },
      { header: 'Duration', accessor: (row) => `${row.durationMinutes || row.duration || 0} min` },
      { header: 'Confirmed', accessor: (row) => `${row.employerConfirmed ? 'Employer' : '-'} / ${row.candidateConfirmed ? 'Candidate' : '-'}` }
    ],
    assessments: [
      { header: 'Assessment', accessor: (row) => row.title },
      { header: 'Role', accessor: (row) => row.job?.title || '-' },
      { header: 'Type', accessor: (row) => labelize(row.type) },
      { header: 'Active', accessor: (row) => row.isActive ? 'Yes' : 'No' },
      { header: 'Questions', accessor: (row) => row.questionCount || (row.questions || []).length || 0 },
      { header: 'Passing', accessor: (row) => `${row.passingScore || 0}%` },
      { header: 'Attempts', accessor: (row) => row.attemptSummary?.total ?? row.attemptsCount ?? 0 },
      { header: 'Completed', accessor: (row) => row.attemptSummary?.completed ?? row.completedCount ?? 0 },
      { header: 'Passed', accessor: (row) => row.attemptSummary?.passed ?? 0 },
      { header: 'Avg Score', accessor: (row) => row.attemptSummary?.averageScore ?? row.averageScore ?? '-' }
    ],
    offers: [
      { header: 'Candidate', accessor: (row) => offerCandidateName(row) },
      { header: 'Role', accessor: (row) => row.job?.title || '-' },
      { header: 'Offer', accessor: (row) => row.title || '-' },
      { header: 'Status', accessor: (row) => labelize(row.status) },
      { header: 'Salary', accessor: (row) => `${formatMoney(row.salaryAmount, row.salaryCurrency)} / ${labelize(row.salaryPeriod).toLowerCase()}` },
      { header: 'Employment', accessor: (row) => labelize(row.employmentType) },
      { header: 'Start', accessor: (row) => formatDate(row.startDate) },
      { header: 'Expires', accessor: (row) => formatDate(row.expiresAt) },
      { header: 'Signature', accessor: (row) => row.signatureRequired ? 'Required' : 'Optional' },
      { header: 'Negotiable', accessor: (row) => row.negotiable ? 'Yes' : 'No' }
    ],
    activity: [
      { header: 'Type', accessor: (row) => labelize(row.type) },
      { header: 'Primary', accessor: (row) => row.primaryLabel || '-' },
      { header: 'Secondary', accessor: (row) => row.secondaryLabel || '-' },
      { header: 'Status', accessor: (row) => labelize(row.status) },
      { header: 'Score', accessor: (row) => row.score ?? '-' },
      { header: 'Amount', accessor: (row) => row.amount ? formatMoney(row.amount) : '-' },
      { header: 'Date', accessor: (row) => formatDateTime(row.happenedAt) }
    ]
  }), []);

  const summaryRows = useMemo(() => buildSummaryRows(dashboard, jobAnalytics), [dashboard, jobAnalytics]);

  const exportSummaryCsv = () => {
    const csv = toCsv(summaryRows, [
      { header: 'Section', accessor: 'section' },
      { header: 'Metric', accessor: 'metric' },
      { header: 'Value', accessor: 'value' }
    ]);
    downloadText(makeReportFilename('summary', timeframe, 'csv'), csv);
    toast.success('Summary CSV exported');
  };

  const exportTabCsv = () => {
    const csv = toCsv(tableRows[activeTab] || [], columns[activeTab] || []);
    downloadText(makeReportFilename(activeTab, timeframe, 'csv'), csv);
    toast.success(`${labelize(activeTab)} CSV exported`);
  };

  const exportFullJson = () => {
    const payload = {
      generatedAt: new Date().toISOString(),
      timeframe,
      dashboard,
      jobAnalytics,
      jobs: report.jobs,
      candidates: report.candidates,
      interviews: report.interviews,
      assessments: report.assessments,
      offers: report.offers
    };
    downloadText(makeReportFilename('full-report', timeframe, 'json'), JSON.stringify(payload, null, 2), 'application/json;charset=utf-8');
    toast.success('Full report JSON exported');
  };

  const printReport = () => {
    window.print();
  };

  if (loading) return <ReportsSkeleton />;

  return (
    <div className="min-h-screen bg-white px-4 py-6 font-sans sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-6">
        <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">Reports</p>
            <h1 className="mt-2 font-display text-3xl font-normal tracking-tight text-[#0b1f3a]">Reports</h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Employer-centric reporting across jobs, candidates, interviews, assessments, offers, and hiring velocity.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-[0_8px_20px_rgba(15,23,42,0.035)]">
              {timeframeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTimeframe(option.value)}
                  className={`h-8 rounded-md px-3 text-xs font-medium transition ${
                    timeframe === option.value ? 'bg-[#0b1f3a] text-white' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {updating && (
              <div className="inline-flex h-10 items-center gap-2 rounded-lg border border-sky-100 bg-[#eef9ff] px-3 text-sm font-medium text-sky-700 shadow-[0_8px_20px_rgba(15,23,42,0.035)]">
                <PiCircleNotchLight className="animate-spin" size={13} />
                Updating
              </div>
            )}
          </div>
        </header>

        <section className="flex flex-col gap-3 rounded-lg border border-slate-200/80 bg-white p-3 shadow-[0_14px_36px_rgba(15,23,42,0.045)] print:hidden lg:flex-row lg:items-center">
          <div className="relative min-w-0 flex-1">
            <PiMagnifyingGlassLight className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
              placeholder="Search loaded report rows"
            />
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
            <button onClick={exportSummaryCsv} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
              <PiDownloadSimpleLight size={13} /> Summary CSV
            </button>
            <button onClick={exportTabCsv} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
              <PiTableLight size={13} /> Current table
            </button>
            <button onClick={exportFullJson} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50">
              <PiFileArrowDownLight size={13} /> JSON
            </button>
            <button onClick={printReport} className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#0b1f3a] px-3 text-sm font-medium text-white transition hover:bg-[#12345c]">
              <PiPrinterLight size={13} /> Print/PDF
            </button>
          </div>
        </section>

        {error && (
          <div className="flex items-start gap-3 rounded-lg border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <PiWarningCircleLight className="mt-0.5 shrink-0" size={14} />
            <span>{error}</span>
          </div>
        )}

        {sourceErrors.length > 0 && (
          <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="mb-1 font-medium">Some report sources did not load</div>
            <div className="space-y-1">
              {sourceErrors.map((item) => <div key={item}>{item}</div>)}
            </div>
          </div>
        )}

        <div
          className={`space-y-6 transition-opacity duration-200 ${updating ? 'opacity-75' : 'opacity-100'}`}
          aria-busy={updating}
        >
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <Metric icon={PiBriefcaseLight} label="Open positions" value={formatNumber(summary.openPositions)} detail={`${formatNumber(summary.activeJobs)} active jobs`} tone="sky" />
              <Metric icon={PiUsersLight} label="Average match score" value={formatPercent(summary.averageMatchScore)} detail={`${formatNumber(summary.totalMatches)} matches`} tone="slate" />
              <Metric icon={PiCheckCircleLight} label="Offer acceptance" value={formatPercent(summary.offerAcceptanceRate)} detail={`${formatNumber(summary.offersAccepted)} accepted`} tone="green" />
              <Metric icon={PiTrendUpLight} label="Offer velocity" value={dashboard.velocity?.averageOfferDays === null || dashboard.velocity?.averageOfferDays === undefined ? 'No data' : `${dashboard.velocity.averageOfferDays}d`} detail="Match to offer" tone="amber" />
            </section>

            <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
              <Panel icon={PiFunnelLight} title="Hiring Funnel" subtitle="Conversion from match generation through filled seats.">
                <Funnel rows={charts.funnel} />
              </Panel>
              <Panel icon={PiChartBarLight} title="Period Trend" subtitle="Monthly jobs, matches, interviews, and offers.">
                <DataTable
                  rows={charts.timeSeries || []}
                  emptyLabel="No trend data available"
                  columns={[
                    { header: 'Month', accessor: 'month' },
                    { header: 'Jobs', accessor: 'jobs' },
                    { header: 'Matches', accessor: 'matches' },
                    { header: 'Interviews', accessor: 'interviews' },
                    { header: 'Offers', accessor: 'offers' }
                  ]}
                />
              </Panel>
            </section>

            <section className="grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
              <Panel icon={PiShieldCheckLight} title="Match Bands">
                <DistributionBars rows={charts.matchBands} />
              </Panel>
              <Panel icon={PiBriefcaseLight} title="Job Status">
                <DistributionBars rows={charts.jobStatus} />
              </Panel>
              <Panel icon={PiSignatureLight} title="Offer Status">
                <DistributionBars rows={charts.offerStatus} />
              </Panel>
              <Panel icon={PiUsersLight} title="Candidate Locations">
                <DistributionBars rows={charts.candidateLocations} />
              </Panel>
            </section>

            <section className="grid gap-4 lg:grid-cols-[0.9fr_1.1fr]">
              <Panel icon={PiWarningCircleLight} title="Skill Gaps" subtitle="Most frequent missing skills from stored match records.">
                <DistributionBars rows={charts.skillGaps || []} labelKey="skill" valueKey="count" />
              </Panel>
              <Panel icon={PiDatabaseLight} title="Executive Signals" subtitle="Backend-generated insights from current employer data.">
                {insights.length ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    {insights.map((insight) => (
                      <div key={insight.id} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                        <div className="text-xs font-medium uppercase tracking-[0.12em] text-slate-400">{insight.label}</div>
                        <div className="mt-1 font-display text-lg font-normal text-[#0b1f3a]">{insight.value}</div>
                        <p className="mt-1 text-sm leading-6 text-slate-600">{insight.detail}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState label="No insights available yet" />
                )}
              </Panel>
            </section>

            <Panel
              icon={PiTableLight}
              title="Operational Tables"
              subtitle={`${formatNumber(tableRows[activeTab]?.length || 0)} rows shown from loaded report data.`}
              action={(
                <div className="flex flex-wrap gap-1.5 print:hidden">
                  {reportTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`h-8 rounded-md px-3 text-xs font-medium transition ${
                        activeTab === tab.id ? 'bg-[#0b1f3a] text-white' : 'border border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              )}
            >
              <DataTable
                rows={tableRows[activeTab] || []}
                columns={columns[activeTab] || []}
                emptyLabel={`No ${activeTab} rows available`}
              />
            </Panel>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
